import { useAuth } from "context/AuthContext";
import {
  AlertCircle,
  BarChart3,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileText,
  LayoutDashboard,
  MessageSquare,
  Users,
} from "lucide-react";
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { hasRole } from "utils/helper";
import logo from "./assets/logo.png";
import { Roles } from "types";

const Sidebar: React.FC<{
  handleLogout?: () => void;
}> = ({ handleLogout }) => {
  const { loginData } = useAuth();
  // enum Roles {
  //   complaintRegistrar = "complaintRegistrar",
  //   // Add other roles as needed
  // }

  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const isMobileAdmin = hasRole(loginData?.roles, [Roles.mobileAppAdmin]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.complaintRegistrar]);

  const toggleDropdown = (title: string) => {
    setOpenDropdown(openDropdown === title ? null : title);
  };

  // Helper function to check if path is active (including nested paths)
  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const navItems = [
    {
      title: "Dashboard",
      path: "/",
      icon: <LayoutDashboard size={16} />,
    },
    {
      title: "Main Task",
      path: "/main-task",
      icon: <ClipboardList size={16} />,
    },
    {
      title: "Reports",
      path: "/reports",
      icon: <BarChart3 size={16} />,
    },
  ];

  const complaintItems = [
    {
      title: "Service Complaint",
      icon: <AlertCircle size={16} />,
      isDropdown: true,
      subItems: [
        {
          title: "Complaint",
          path: "/complaint",
        },
        {
          title: "Appeal",
          path: "/appeal",
        },
      ],
    },
    {
      title: "Complaint on Prosecutor Case",
      icon: <FileText size={16} />,
      isDropdown: true,
      subItems: [
        {
          title: "Complaint",
          path: "/civil-case-complaint",
        },
        {
          title: "Appeal",
          path: "/civil-appeal",
        },
      ],
    },
    !isRegistrar && {
      title: "Feedback",
      path: "/feedback",
      icon: <MessageSquare size={16} />,
    },
  ];

  const adminItems = [
    {
      title: "User",
      path: "/user",
      icon: <Users size={16} />,
    },
  ];

  const navItem = (item: {
    title: string;
    path: string;
    icon: React.ReactNode;
  }) => {
    const isItemActive = isActive(item.path);

    return (
      <Link to={item.path} key={item.path}>
        <div
          className={`flex items-center px-3 py-2 rounded font-medium transition-colors ${
            isItemActive
              ? "bg-white text-black"
              : "hover:bg-gray-800 hover:text-white"
          }`}
        >
          {item.icon && <span className="mr-3 text-sm">{item.icon}</span>}
          {item.title}
        </div>
      </Link>
    );
  };

  const renderComplaintItem = (
    item: {
      title: string;
      icon: React.ReactNode;
      path?: string;
      isDropdown?: boolean;
      subItems?: { title: string; path: string }[];
    },
    index: number,
  ) => {
    if (item.isDropdown && item.subItems) {
      const isDropdownOpen = openDropdown === item.title;
      const isAnySubItemActive = item.subItems.some((subItem) =>
        isActive(subItem.path),
      );

      return (
        <div key={index} className="mb-1">
          <button
            onClick={() => toggleDropdown(item.title)}
            className={`flex items-center justify-between w-full px-3 py-2 rounded font-medium transition-colors text-left ${
              isAnySubItemActive
                ? "bg-white text-black"
                : "hover:bg-gray-800 hover:text-white"
            }`}
          >
            <div className="flex items-center text-left">
              {item.icon && <span className="mr-3 text-sm">{item.icon}</span>}
              {item.title}
            </div>
            <span className="text-xs">
              {isDropdownOpen ? (
                <ChevronDown size={12} />
              ) : (
                <ChevronRight size={12} />
              )}
            </span>
          </button>

          {isDropdownOpen && (
            <div className="mt-1 ml-2 space-y-1">
              {item.subItems.map((subItem) => {
                const isSubItemActive = isActive(subItem.path);
                return (
                  <Link to={subItem.path} key={subItem.path}>
                    <div
                      className={`flex items-center pl-8 pr-3 py-2 text-sm rounded font-medium transition-colors ${
                        isSubItemActive
                          ? "bg-gray-700 text-white"
                          : "hover:bg-gray-800 text-gray-300"
                      }`}
                    >
                      {subItem.title}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Regular item without dropdown
    const isItemActive = item.path ? isActive(item.path) : false;

    return (
      <Link to={item.path!} key={item.path}>
        <div
          className={`flex items-center px-3 py-2 rounded font-medium transition-colors ${
            isItemActive
              ? "bg-white text-black"
              : "hover:bg-gray-800 hover:text-white"
          }`}
        >
          {item.icon && <span className="mr-3 text-sm">{item.icon}</span>}
          {item.title}
        </div>
      </Link>
    );
  };

  return (
    <aside className="flex-col justify-between hidden w-64 min-h-screen text-white bg-black md:flex">
      <div className="p-4">
        {/* Logo Section */}
        <div className="flex items-center gap-2 mb-4">
          <img src={logo} alt="Logo" className="w-12 h-10 rounded" />
          <div className="text-xs leading-tight">
            <div className="text-sm font-semibold">
              Biiroo Abba Alangaa W.O.
            </div>
            <div className="text-gray-400 text-[11px]">
              የኦሮሚያ ጠቅላይ ዐቃቤ ሕግ ቢሮ
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col space-y-1 text-sm">
          <Section title="DASHBOARD">
            {navItems.map((item) => navItem(item))}
          </Section>

          <Section title="COMPLAINT CASE">
            <div className="space-y-1">
              {!isMobileAdmin &&
                complaintItems.map((item: any, index) =>
                  renderComplaintItem(item, index),
                )}

              {isMobileAdmin &&
                adminItems.map((item, index) =>
                  renderComplaintItem(item, index),
                )}
            </div>
          </Section>
        </nav>
      </div>

      {/* Footer */}
      <div className="p-4 text-xs text-gray-400">
        Powered by EAII
        {/* {handleLogout && (
          <button
            className="block mt-2 text-white font-medium hover:underline"
            onClick={handleLogout}
          >
            Logout
          </button>
        )} */}
      </div>
    </aside>
  );
};

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4">
      <div className="mb-2 text-xs font-semibold text-gray-400 uppercase">
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

export default Sidebar;
